/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { ActionRequest, ActionSuccess, ActionFail, Folder, Asset, XError } from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, put, select, call, takeEvery } from 'redux-saga/effects';
import { Selectors as SubscriptionsFoldersSelectors } from 'core/store/ducks/entitlements/assets/subscriptionsFolders';
import { loadAndGetAssetsSaga } from 'core/store/ducks/assets';
import Constants from 'configs/Constants';
import { StoreState } from 'store/store';

enum ActionType {
  GET_FOLDER_ASSETS = 'myTv/subscriptions/GET_FOLDER_ASSETS',
  GET_FOLDER_ASSETS_SUCCESS = 'myTv/subscriptions/GET_FOLDER_ASSETS_SUCCESS',
  GET_FOLDER_ASSETS_FAIL = 'myTv/subscriptions/GET_FOLDER_ASSETS_FAIL',
}

interface GetFolderAssetsMeta {
  folderId: number;
  channelId: number;
}

interface GetFolderAssetsPayload {
  folderId: number;
  channelId: number;
  folder: Folder;
  data: Asset[];
}

type GetFolderAssetsRequest = ActionRequest<ActionType.GET_FOLDER_ASSETS, GetFolderAssetsMeta>;
type GetFolderAssetsSuccess = ActionSuccess<ActionType.GET_FOLDER_ASSETS_SUCCESS, GetFolderAssetsPayload>;
type GetFolderAssetsFail = ActionFail<ActionType.GET_FOLDER_ASSETS_FAIL, GetFolderAssetsMeta>;

export type Actions = GetFolderAssetsRequest | GetFolderAssetsSuccess | GetFolderAssetsFail;

const Actions = {
  getFolderAssets: (folderId: number, channelId: number): GetFolderAssetsRequest => ({
    type: ActionType.GET_FOLDER_ASSETS,
    meta: {
      folderId,
      channelId,
    },
  }),
  getFolderAssetsSuccess: (
    folderId: number,
    channelId: number,
    folder: Folder,
    assets: Asset[]
  ): GetFolderAssetsSuccess => ({
    type: ActionType.GET_FOLDER_ASSETS_SUCCESS,
    payload: {
      folderId,
      channelId,
      folder,
      data: assets,
    },
  }),
  getFolderAssetsFail: (folderId: number, channelId: number, error: XError): GetFolderAssetsFail => ({
    type: ActionType.GET_FOLDER_ASSETS_FAIL,
    error,
    meta: { folderId, channelId },
  }),
};

export const ActionCreators = {
  getFolderAssets: (folderId: number, channelId: number): GetFolderAssetsRequest =>
    Actions.getFolderAssets(folderId, channelId),
};

export interface ReducerState {
  [key: string]: { data: Asset[]; folder?: Folder; processing?: boolean; processed?: boolean; error?: XError };
}

const initialState: ReducerState = {};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.GET_FOLDER_ASSETS:
      return {
        ...state,
        [`${action.meta.folderId}_${action.meta.channelId}`]: {
          data: [],
          processing: true,
          processed: false,
          error: undefined, // reset error
        },
      };
    case ActionType.GET_FOLDER_ASSETS_SUCCESS:
      return {
        ...state,
        [`${action.payload.folderId}_${action.payload.channelId}`]: {
          data: action.payload.data,
          folder: action.payload.folder,
          processing: false,
          processed: true,
        },
      };
    case ActionType.GET_FOLDER_ASSETS_FAIL:
      return {
        ...state,
        [`${action.meta.folderId}_${action.meta.channelId}`]: {
          data: [],
          processing: false,
          processed: true,
          error: action.error,
        },
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  isFolderAssetsProcessing: (state: StoreState, folderId?: number, channelId?: number): boolean | undefined => {
    if (folderId && channelId && state.myTv.folderAssets && state.myTv.folderAssets[`${folderId}_${channelId}`]) {
      return state.myTv.folderAssets[`${folderId}_${channelId}`].processing;
    }
    return undefined;
  },
  isFolderAssetsProcessed: (state: StoreState, folderId?: number, channelId?: number): boolean | undefined => {
    if (folderId && channelId && state.myTv.folderAssets && state.myTv.folderAssets[`${folderId}_${channelId}`]) {
      return state.myTv.folderAssets[`${folderId}_${channelId}`].processed;
    }
    return undefined;
  },
  getFolderAssets: (state: StoreState, folderId?: number, channelId?: number): Asset[] | undefined => {
    if (folderId && channelId && state.myTv.folderAssets && state.myTv.folderAssets[`${folderId}_${channelId}`]) {
      return state.myTv.folderAssets[`${folderId}_${channelId}`].data;
    }
    return undefined;
  },
};

/* Helping functions */
function* getFolderAssetsSaga(action: GetFolderAssetsRequest): SagaIterator {
  const { folderId, channelId } = action.meta;
  try {
    const folders: Folder[] = yield select(SubscriptionsFoldersSelectors.getFolders);
    const folder = folders.find((e) => e.id === folderId);

    if (folder) {
      let assets: Asset[] | undefined = yield call(
        loadAndGetAssetsSaga,
        channelId,
        Constants.SUBSCRIPTIONS_ITEMS_DISPLAYED
      );
      if (assets?.length) {
        assets.unshift(folder);
      } else {
        assets = [];
      }

      yield put(Actions.getFolderAssetsSuccess(folderId, channelId, folder, assets));
    }
  } catch (error) {
    yield put(Actions.getFolderAssetsFail(folderId, channelId, XError.fromError(error as Error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionType.GET_FOLDER_ASSETS, getFolderAssetsSaga)]);
}
